import { useRouter } from 'next/router'
import { useCallback, useRef } from 'react'

import useBeforePopState from 'app/KinerjaShared/hooks/useBeforePopState'

export type UseBackNavigationConfirmationOptions = {
  /**
   * When true, browser back is intercepted and `onInterceptBack` is invoked.
   */
  shouldConfirm: boolean

  /**
   * Called when browser back is intercepted. Typically opens a confirmation
   * dialog. May be passed inline; the hook keeps a ref so `beforePopState` is
   * not re-registered on every render.
   */
  onInterceptBack: () => void
}

/**
 * Intercepts browser history back navigation when confirmation is required.
 *
 * Registers a `beforePopState` handler that blocks back navigation and invokes
 * `onInterceptBack` (e.g. open a confirmation dialog) instead of leaving the page.
 */
export function useBackNavigationConfirmation({
  shouldConfirm,
  onInterceptBack,
}: UseBackNavigationConfirmationOptions) {
  const router = useRouter()

  // Keep latest callback without adding it to useCallback deps. Inline handlers
  // (e.g. () => setActiveDialog(...)) change every render; including them would
  // recreate handleBackConfirmation and re-run useBeforePopState's effect each time.
  const onInterceptBackRef = useRef(onInterceptBack)
  onInterceptBackRef.current = onInterceptBack

  const handleBackConfirmation = useCallback(() => {
    if (shouldConfirm) {
      onInterceptBackRef.current()
      return false
    }

    return true
  }, [shouldConfirm])

  useBeforePopState(router, handleBackConfirmation)
}
